import { ApiError, type ErrorCode } from '@brig/api-types';
import type { z } from 'zod';

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'network',
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }

  /** Field-level validation problems keyed by field path. */
  fieldErrors(): Record<string, string> {
    if (!Array.isArray(this.details)) return {};
    const result: Record<string, string> = {};
    for (const item of this.details as { path?: unknown; message?: unknown }[]) {
      if (typeof item.path === 'string' && typeof item.message === 'string')
        result[item.path] ??= item.message;
    }
    return result;
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  getAccessToken: () => Promise<string | null>;
  /** Called once after a 401 to obtain a fresh token; return null to give up. */
  refreshAccessToken: () => Promise<string | null>;
  /** Preferred language, sent so that a brand-new profile starts in the UI language. */
  getLocale: () => string;
  fetchImpl?: typeof fetch;
}

interface RequestOptions<S extends z.ZodType> {
  body?: unknown;
  schema: S;
  signal?: AbortSignal;
}

export function createApiClient(options: ApiClientOptions) {
  const doFetch = options.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));

  async function send(
    method: string,
    path: string,
    token: string | null,
    body: unknown,
    signal?: AbortSignal,
  ) {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Accept-Language': options.getLocale(),
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    try {
      return await doFetch(`${options.baseUrl}${path}`, {
        method,
        headers,
        body: body === undefined ? null : JSON.stringify(body),
        signal: signal ?? null,
      });
    } catch (error) {
      if ((error as Error).name === 'AbortError') throw error;
      throw new ApiRequestError(0, 'network', 'Network request failed');
    }
  }

  async function request<S extends z.ZodType>(
    method: string,
    path: string,
    { body, schema, signal }: RequestOptions<S>,
  ): Promise<z.infer<S>> {
    let response = await send(method, path, await options.getAccessToken(), body, signal);
    if (response.status === 401) {
      const fresh = await options.refreshAccessToken();
      if (fresh) response = await send(method, path, fresh, body, signal);
    }

    const payload: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const parsed = ApiError.safeParse(payload);
      if (parsed.success) {
        const { code, message, details } = parsed.data.error;
        throw new ApiRequestError(response.status, code, message, details);
      }
      throw new ApiRequestError(
        response.status,
        response.status === 401 ? 'unauthorized' : 'internal',
        response.statusText,
      );
    }
    return schema.parse(payload);
  }

  return {
    get: <S extends z.ZodType>(path: string, schema: S, signal?: AbortSignal) =>
      request('GET', path, { schema, ...(signal ? { signal } : {}) }),
    post: <S extends z.ZodType>(path: string, body: unknown, schema: S) =>
      request('POST', path, { schema, body }),
    patch: <S extends z.ZodType>(path: string, body: unknown, schema: S) =>
      request('PATCH', path, { schema, body }),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
